'use client'

import { useState } from 'react'
import { toast } from 'sonner'
import {
  ArrowRightLeft,
  AlertTriangle,
  Loader2,
  Crown,
  Shield,
  CheckCircle2,
  User,
} from 'lucide-react'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { popModal } from '@/components/modals'
import { useTranslations } from 'next-intl'

interface Member {
  _id: string
  user_id: string
  user: {
    _id: string
    email: string
    name: string
    avatar?: string
  } | null
  role: string
  display_role: string
}

interface TransferOwnershipModalProps {
  workspaceId: string
  workspaceName: string
  members: Member[]
  onSuccess?: () => void
}

export default function TransferOwnershipModal({
  workspaceId,
  workspaceName,
  members,
  onSuccess,
}: TransferOwnershipModalProps) {
  const t = useTranslations('transfer-ownership')
  const [selectedMemberId, setSelectedMemberId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const selectedMember = members.find((m) => m.user_id === selectedMemberId)

  const handleTransfer = async () => {
    if (!selectedMemberId) {
      toast.error(t('errors.select_owner'))
      return
    }

    setIsLoading(true)
    try {
      const res = await fetch(
        `/api/me/workspaces/${workspaceId}/transfer-ownership`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_owner_id: selectedMemberId }),
        },
      )
      const data = await res.json()

      if (data.success) {
        setSuccess(true)
        onSuccess?.()
      } else {
        toast.error(data.message || t('errors.failed_initiate'))
      }
    } catch (error) {
      toast.error(t('errors.failed_initiate'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <DialogContent
      className={cn(
        'sm:max-w-[480px] max-w-[95vw]',
        'bg-[#0f0f0f] border border-gold/20',
        'shadow-[0_0_60px_rgba(255,215,0,0.1)]',
        'backdrop-blur-xl',
        'p-0 gap-0 overflow-hidden',
        'rounded-2xl',
      )}
      overlayClassName="bg-black/80 backdrop-blur-sm"
    >
      {/* Success State */}
      {success ? (
        <div className="p-8 text-center">
          <div
            className={cn(
              'w-16 h-16 mx-auto mb-6',
              'rounded-full bg-emerald-500/10',
              'flex items-center justify-center',
              'ring-4 ring-emerald-500/20',
            )}
          >
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>

          <h3 className="text-xl font-bold text-white mb-2">
            {t('success.title')}
          </h3>
          <p className="text-white/60 mb-2">{t('success.email_sent')}</p>
          <p className="text-gold font-medium mb-6">
            {selectedMember?.user?.email}
          </p>

          <div
            className={cn(
              'p-4 rounded-xl mb-6',
              'bg-gold/5 border border-gold/20',
            )}
          >
            <p className="text-white/70 text-sm">{t('success.info_message')}</p>
          </div>

          <Button
            onClick={() => popModal()}
            className={cn(
              'w-full h-11',
              'bg-gold hover:bg-gold/90',
              'text-black font-semibold',
            )}
          >
            {t('actions.done')}
          </Button>
        </div>
      ) : (
        <>
          {/* Header */}
          <div
            className={cn(
              'p-6 pb-0',
              'bg-gradient-to-b from-gold/5 to-transparent',
            )}
          >
            <div className="flex items-center gap-3 mb-2">
              <div
                className={cn(
                  'w-10 h-10 rounded-xl',
                  'bg-gold/10 border border-gold/20',
                  'flex items-center justify-center',
                )}
              >
                <ArrowRightLeft className="w-5 h-5 text-gold" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-white">
                  {t('title')}
                </DialogTitle>
                <DialogDescription className="text-sm text-white/50">
                  {workspaceName}
                </DialogDescription>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-5">
            {/* Warning */}
            <div
              className={cn(
                'p-4 rounded-xl',
                'bg-yellow-500/10 border border-yellow-500/20',
                'flex items-start gap-3',
              )}
            >
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-500 font-medium text-sm">
                  {t('warnings.important')}
                </p>
                <p className="text-white/60 text-sm mt-1">
                  {t('warnings.description')}
                </p>
              </div>
            </div>

            {/* Member Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/70">
                {t('labels.select_owner')}
              </label>

              <Select
                value={selectedMemberId}
                onValueChange={setSelectedMemberId}
              >
                <SelectTrigger
                  className={cn(
                    'h-14 rounded-xl',
                    'bg-[#0a0a0a] border-gold/20',
                    'text-white',
                    'focus:border-gold/50 focus:ring-gold/20',
                  )}
                >
                  <SelectValue placeholder={t('placeholders.select_member')} />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-gold/20">
                  {members.map((member) => (
                    <SelectItem
                      key={member.user_id}
                      value={member.user_id}
                      className="text-white hover:bg-gold/10 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={member.user?.avatar} />
                          <AvatarFallback className="bg-gold/10 text-gold text-sm">
                            {member.user?.name?.charAt(0) ||
                              member.user?.email?.charAt(0) ||
                              '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="text-left">
                          <p className="font-medium">
                            {member.user?.name || t('labels.unknown')}
                          </p>
                          <p className="text-xs text-white/50">
                            {member.user?.email}
                          </p>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Preview of what happens */}
            {selectedMember && (
              <div
                className={cn(
                  'p-4 rounded-xl',
                  'bg-[#0a0a0a] border border-gold/10',
                  'space-y-3',
                )}
              >
                <p className="text-xs text-white/40 uppercase tracking-wider">
                  {t('labels.after_transfer')}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={selectedMember.user?.avatar} />
                      <AvatarFallback className="bg-gold/10 text-gold">
                        {selectedMember.user?.name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-white">
                        {selectedMember.user?.name}
                      </p>
                      <p className="text-xs text-white/50">
                        {selectedMember.user?.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold/10 text-gold text-xs font-medium">
                    <Crown className="w-3.5 h-3.5" />
                    {t('roles.owner')}
                  </div>
                </div>

                <div className="border-t border-gold/10 pt-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-white/60" />
                      </div>
                      <div>
                        <p className="font-medium text-white/70">
                          {t('labels.you')}
                        </p>
                        <p className="text-xs text-white/40">
                          {t('roles.current_owner')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-medium">
                      <Shield className="w-3.5 h-3.5" />
                      {t('roles.admin')}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Final warning */}
            <div
              className={cn(
                'p-4 rounded-xl',
                'bg-red-500/5 border border-red-500/20',
              )}
            >
              <p className="text-red-400 text-sm">{t('warnings.final')}</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline_2"
                onClick={() => popModal()}
                className="flex-1 h-11 border-gold/20 text-white hover:bg-gold/10"
              >
                {t('actions.cancel')}
              </Button>
              <Button
                onClick={handleTransfer}
                disabled={!selectedMemberId || isLoading}
                className={cn(
                  'flex-1 h-11',
                  'bg-gold hover:bg-gold/90',
                  'text-black font-semibold',
                  'disabled:opacity-50',
                )}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  t('actions.send')
                )}
              </Button>
            </div>
          </div>
        </>
      )}
    </DialogContent>
  )
}
